import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { forumApi } from '../../api/forum';
import { UserContext } from '../../context/UserContext';
import { isAdmin, isModerator } from '../../admin/utils/roles';
import HrnbCard from '../controls/HrnbCard';
import HrnbButton from '../controls/HrnbButton';
import HrnbModal from '../controls/HrnbModal';
import HrnbLoader from '../controls/HrnbLoader';
import TagBadge from './TagBadge';
import '../../css/components/PostCard.css';

export default function PostCard({ post }) {
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);
    const [liked, setLiked] = useState(post.liked || false);
    const [likeCount, setLikeCount] = useState(post.likes || post.likeCount || 0);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const handleViewDetails = () => {
        navigate(`/forum/posts/${post.id}`);
    };

    const handleLike = async (e) => {
        e.stopPropagation();
        if (!currentUser) {
            return;
        }
        try {
            if (liked) {
                await forumApi.unlikePost(post.id);
                setLikeCount(prev => prev - 1);
            } else {
                await forumApi.likePost(post.id);
                setLikeCount(prev => prev + 1);
            }
            setLiked(!liked);
        } catch (err) {
            console.error('[PostCard] Like failed:', err.message);
        }
    };

    const handleDeletePost = async () => {
        if (!currentUser) {
            return;
        }

        const authorProfileId = currentUser.profiles?.[0]?.id;
        const isAuthor = post.author?.id === authorProfileId || post.author?.userId === currentUser.id;
        const canDelete = isAuthor || isAdmin(currentUser) || isModerator(currentUser);

        if (!canDelete) {
            return;
        }

        try {
            setDeleting(true);
            await forumApi.deletePost(post.id);
            setDeleteModalOpen(false);
            // Перезагружаем страницу списка постов
            window.location.reload();
        } catch (err) {
            console.error('[PostCard] Failed to delete post:', err.message);
        } finally {
            setDeleting(false);
        }
    };

    const canDeletePost = () => {
        if (!currentUser) return false;
        const authorProfileId = currentUser.profiles?.[0]?.id;
        const isAuthor = post.author?.id === authorProfileId || post.author?.userId === currentUser.id;
        return isAuthor || isAdmin(currentUser) || isModerator(currentUser);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'Не указано';
        const date = new Date(dateStr);
        return date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    const {
        title,
        content,
        author,
        category,
        tags = [],
        createdAt,
        commentsCount = 0,
        viewCount = 0,
    } = post;

    return (
        <HrnbCard className="post-card">
            <div className="post-card-header">
                <h3 className="post-card-title">{title}</h3>
                {category && (
                    <span className="post-card-category">
                        {typeof category === 'string' ? category : (category.name || 'Категория')}
                    </span>
                )}
            </div>

            <div className="post-card-body">
                {content && (
                    <p className="post-card-content">
                        {content.length > 150
                            ? `${content.substring(0, 150)}...`
                            : content}
                    </p>
                )}

                <div className="post-card-meta">
                    {author && (
                        <span className="post-card-author">
                            Автор: {typeof author === 'string' ? author : (author.displayName || 'Аноним')}
                        </span>
                    )}
                    {createdAt && (
                        <span className="post-card-date">
                            {formatDate(createdAt)}
                        </span>
                    )}
                </div>

                {tags && tags.length > 0 && (
                    <div className="post-card-tags">
                        {tags.slice(0, 3).map((tag, index) => (
                            <TagBadge
                                key={tag.id || index}
                                tag={tag}
                                variant="default"
                            />
                        ))}
                    </div>
                )}
            </div>

            <div className="post-card-footer" onClick={handleViewDetails} style={{ cursor: 'pointer' }}>
                <div className="post-card-stats">
                    <span className="post-card-stat">
                        👁 {viewCount}
                    </span>
                    <span className="post-card-stat">
                        💬 {commentsCount}
                    </span>
                    <span
                        className={`post-card-stat post-card-like-clickable ${liked ? 'post-card-liked' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleLike(e);
                        }}
                        title={currentUser ? 'Нажмите для лайка' : 'Войдите, чтобы поставить лайк'}
                    >
                        👍 {likeCount}
                    </span>
                    {canDeletePost() && (
                        <span
                            className="post-card-stat post-card-delete-clickable"
                            onClick={(e) => {
                                e.stopPropagation();
                                setDeleteModalOpen(true);
                            }}
                            title="Удалить пост"
                        >
                            🗑️
                        </span>
                    )}
                </div>
            </div>

            {/* Модальное окно подтверждения удаления поста */}
            <HrnbModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                title="Удаление поста"
                size="small"
            >
                <div className="delete-modal-content">
                    <p className="delete-modal-text">
                        Вы уверены, что хотите удалить пост «{title}»? Это действие нельзя отменить.
                    </p>
                    <div className="delete-modal-actions">
                        <HrnbButton
                            variant="outline"
                            onClick={() => setDeleteModalOpen(false)}
                            disabled={deleting}
                        >
                            Отмена
                        </HrnbButton>
                        <HrnbButton
                            variant="danger"
                            onClick={handleDeletePost}
                            disabled={deleting}
                        >
                            {deleting ? <HrnbLoader size="sm" /> : 'Удалить'}
                        </HrnbButton>
                    </div>
                </div>
            </HrnbModal>
        </HrnbCard>
    );
}
